import setGlobalState from '../setGlobalState';
import getStoreByPath from '../../functions/getStoreByPath';

interface PaginatedRequestOptions<T = any> {
    limit?: number;
    offset?: number;
    onFirstLoad?: (response: any, items: T[]) => void;
    onEachPage?: (response: any, items: T[]) => void;
    shouldContinue?: (response: any, newItems: T[], allItems: T[]) => boolean;
}

/**
 * Generic function for recursive pagination fetching
 * Similar to copyUsers pattern - receives a callback that makes the API request
 * @param {Function} requestCallback - Callback function that makes the API request (limit, offset) => Promise
 * @param {string} responseKey - Key in response that contains the array (e.g., 'ticketsInfo', 'priceGroups', 'leads')
 * @param {string} statePath - Redux state path to store the items (e.g., 'tickets.tickets', 'subscription.priceGroups')
 * @param {PaginatedRequestOptions} [options={}] - Pagination options
 * @returns {Promise<Array>} - The accumulated items array
 */
export const paginatedRequest = async <T = any>(
    requestCallback: (limit: number, offset: number) => Promise<any>,
    responseKey: string,
    statePath: string,
    {
        limit = 10,
        offset: initialOffset,
        onFirstLoad,
        onEachPage,
        shouldContinue
    }: PaginatedRequestOptions<T> = {}
): Promise<T[]> => {
    const currentItems = (getStoreByPath(statePath) || []) as T[];
    const offset = initialOffset !== undefined ? initialOffset : currentItems.length;

    const res = await requestCallback(limit, offset);
    
    if(!res || !res[responseKey]) {
        return currentItems;
    }
    
    const newItems = res[responseKey] as T[];
    const allItems = [...currentItems, ...newItems];
    
    // Update state
    setGlobalState(statePath, allItems);
    
    // Call callbacks
    if(offset === 0 && onFirstLoad) {
        onFirstLoad(res, allItems);
    }
    if(onEachPage) {
        onEachPage(res, allItems);
    }
    
    // Check if should continue
    const shouldContinueFetch = shouldContinue 
        ? shouldContinue(res, newItems, allItems)
        : newItems.length >= limit;
    
    if(shouldContinueFetch) {
        return await paginatedRequest(
            requestCallback,
            responseKey,
            statePath,
            {
                limit,
                offset: allItems.length, // Pass the calculated offset based on accumulated items
                onFirstLoad,
                onEachPage,
                shouldContinue
            }
        );
    }
    
    return allItems;
};

